import {
  PartnerSearchByBrResponse,
  SafeDepositBoxServiceRoleBulkItem,
  ServiceRole,
  SignatureAuthority,
} from 'pco-product-service-internal-consumer';
import { isSafeDepositBoxServiceRoleProperties } from '../../../productSpecificProperties';

// These are backend contract values, not display labels. User-facing role names are translated in the grid.
export const SAFE_HOLDER_API_VALUE = 'Safe holder';
export const SAFE_POWER_OF_ATTORNEY_API_VALUE = 'Safe Power of Attorney';

export const hasValidSafeDepositBoxSafeRole = (serviceRole: ServiceRole) => {
  const properties = serviceRole.productSpecificProperties;
  return !isSafeDepositBoxServiceRoleProperties(properties) || Boolean(properties.safeRole);
};

export type SafeDepositBoxServiceRoleDraft = {
  key: string;
  serviceRoleUid?: string;
  partnerUid: string;
  partnerNumber?: string;
  fullName?: string;
  partnerType?: string;
  domicileCountry?: string;
  safeRole: string;
  signatureAuthority?: SignatureAuthority;
  isEditable: boolean;
  isRemovable: boolean;
  isNew: boolean;
};

export const buildSafeDepositBoxDraftFromServiceRole = (
  serviceRole: ServiceRole,
): SafeDepositBoxServiceRoleDraft | undefined => {
  const properties = serviceRole.productSpecificProperties;
  if (!isSafeDepositBoxServiceRoleProperties(properties) || !hasValidSafeDepositBoxSafeRole(serviceRole)) {
    return undefined;
  }
  const isSafeHolder = properties.safeRole === SAFE_HOLDER_API_VALUE;

  return {
    key: serviceRole.serviceRoleUid ?? serviceRole.partnerUid,
    serviceRoleUid: serviceRole.serviceRoleUid,
    partnerUid: serviceRole.partnerUid,
    partnerNumber: serviceRole.partnerNumber,
    fullName:
      serviceRole.fullName ?? ([serviceRole.firstName, serviceRole.lastName].filter(Boolean).join(' ') || undefined),
    partnerType: serviceRole.partnerType,
    domicileCountry: serviceRole.domicileCountry,
    safeRole: properties.safeRole,
    signatureAuthority: properties.signatureAuthority ?? undefined,
    isEditable: !isSafeHolder && (serviceRole.isEditable ?? false),
    isRemovable: serviceRole.isRemovable ?? false,
    isNew: false,
  };
};

export const buildSafeDepositBoxDraftFromPartner = (
  partner: PartnerSearchByBrResponse,
): SafeDepositBoxServiceRoleDraft => ({
  key: partner.techId ?? partner.globalPartnerId ?? `new-${partner.fullName ?? 'partner'}`,
  partnerUid: partner.techId ?? '',
  partnerNumber: partner.globalPartnerId,
  fullName: partner.fullName,
  partnerType: partner.partnerType,
  domicileCountry: partner.mainDomicileCountry,
  safeRole: SAFE_POWER_OF_ATTORNEY_API_VALUE,
  signatureAuthority: undefined,
  isEditable: true,
  isRemovable: true,
  isNew: true,
});

/** The safe holder's role and signature authority come from the business relation, so the modal never edits them. */
export const isEditableServiceRoleRow = (row: SafeDepositBoxServiceRoleDraft) =>
  row.isEditable && row.safeRole !== SAFE_HOLDER_API_VALUE;

export type SafeDepositBoxServiceRoleError = 'signatureAuthorityRequired' | null;

type ServiceRoleErrorOptions = {
  signatureAuthorityValues: ReadonlySet<string>;
  touchedRows: ReadonlySet<string>;
};

export const getSafeDepositBoxServiceRoleError = (
  row: SafeDepositBoxServiceRoleDraft,
  { signatureAuthorityValues }: ServiceRoleErrorOptions,
): SafeDepositBoxServiceRoleError => {
  if (!row.signatureAuthority) return 'signatureAuthorityRequired';
  if (isEditableServiceRoleRow(row) && !signatureAuthorityValues.has(row.signatureAuthority)) {
    return 'signatureAuthorityRequired';
  }
  return null;
};

/** Mirrors the configuration fields: a gap is only reported once the user is responsible for it. */
export const getSafeDepositBoxServiceRoleErrors = (
  rows: SafeDepositBoxServiceRoleDraft[],
  options: ServiceRoleErrorOptions,
): ReadonlyMap<string, SafeDepositBoxServiceRoleError> =>
  new Map(
    rows
      .filter((row) => row.isNew || options.touchedRows.has(row.key))
      .map((row) => [row.key, getSafeDepositBoxServiceRoleError(row, options)] as const)
      .filter(([, error]) => error !== null),
  );

export const hasCompleteSafeDepositBoxServiceRoles = (
  rows: SafeDepositBoxServiceRoleDraft[],
  options: ServiceRoleErrorOptions,
) => rows.every((row) => !!row.partnerUid && getSafeDepositBoxServiceRoleError(row, options) === null);

export const buildPartnerFromSafeDepositBoxDraft = (
  draft: SafeDepositBoxServiceRoleDraft,
): PartnerSearchByBrResponse => ({
  techId: draft.partnerUid,
  globalPartnerId: draft.partnerNumber,
  fullName: draft.fullName,
  partnerType: draft.partnerType,
  mainDomicileCountry: draft.domicileCountry,
});

const normalizedSnapshot = (rows: SafeDepositBoxServiceRoleDraft[]) =>
  rows
    .map(({ partnerUid, safeRole, signatureAuthority }) => ({ partnerUid, safeRole, signatureAuthority }))
    .sort((left, right) => left.partnerUid.localeCompare(right.partnerUid));

export const haveSameSafeDepositBoxServiceRoles = (
  left: SafeDepositBoxServiceRoleDraft[],
  right: SafeDepositBoxServiceRoleDraft[],
) => JSON.stringify(normalizedSnapshot(left)) === JSON.stringify(normalizedSnapshot(right));

export const buildSafeDepositBoxServiceRolePayload = (
  rows: SafeDepositBoxServiceRoleDraft[],
): SafeDepositBoxServiceRoleBulkItem[] =>
  rows.map((row) => {
    if (!row.partnerUid || !row.safeRole || !row.signatureAuthority) {
      throw new Error('Cannot build a Safe Deposit Box service-role payload from an incomplete row');
    }

    return {
      partnerUid: row.partnerUid,
      safeRole: row.safeRole,
      signatureAuthority: row.signatureAuthority,
    };
  });
